import { useState } from 'react';

import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography
} from '@mui/material';

import {
  AttachFileOutlined,
  CheckCircleOutlined,
  CloseOutlined,
  PersonOutlined
} from '@mui/icons-material';

import PatientAutocomplete
  from '../../Common/components/Patients/PatientAutocomplete';

import CommonDialogActions
  from '../../Common/components/CommonDialogActions';

import {
  attachButtonSx,
  dialogHeaderStyles,
  issueTextSx,
  multitextFieldSx,
  patientBoxSx,
  patientMrSx,
  patientNameSx,
  sectionTitleSx,
  timelineContentSx,
  timelineIconWrapperSx,
  timelineItemSx,
  timelineMessageSx,
  timelineMetaSx,
  timelineTitleSx,
  timelineUserIconSx,
  timelineUserSx
} from '../../Common/components/Styles/InputStyles';

import {
  closeIconSx,
  completedTimelineIconSx,
  dialogContentSx,
  getStatusStyle,
  patientHeaderRowSx,
  responseSectionSx,
  statusChipSx
} from './PTMessageModalStyles';

const patients = [
  {
    name: 'James Hunt',
    mr: '21040897',
    dob: '03/06/2006 (20 yrs)',
    sex: 'Male'
  },
  {
    name: 'Philip Cammee John',
    mr: '22351612',
    dob: '05/12/1985 (40 yrs)',
    sex: 'Male'
  },
  {
    name: 'Jennifer Lee',
    mr: '2318634',
    dob: '08/18/1992 (33 yrs)',
    sex: 'Female'
  },
  {
    name: 'Olivia Martin',
    mr: '21678902',
    dob: '11/25/1988 (37 yrs)',
    sex: 'Female'
  },
  {
    name: 'Robert Wilson',
    mr: '21985642',
    dob: '02/14/1975 (51 yrs)',
    sex: 'Male'
  }
];

const initialForm = {
  patient: null,

  patientMessage:
    'Dr. reviewed your Mammogram and stated the Mammogram looks good, nothing to be concerned, however, recommend repeat mammogram in 12 months.'
};

export const CreateMessageModal = ({
  open,
  onClose
}) => {
  const [form, setForm] =
    useState(initialForm);

  const handleSelectPatient = patient => {
    setForm(previousForm => ({
      ...previousForm,
      patient
    }));
  };

  const handleMessageChange = event => {
    setForm(previousForm => ({
      ...previousForm,

      patientMessage:
        event.target.value
    }));
  };

  const handleClose = () => {
    setForm(initialForm);
    onClose();
  };

  const handleSendMessage = () => {
    console.log(
      'Message data:',
      form
    );

    // Call your API or parent callback here.

    handleClose();
  };

  const isValid = Boolean(
    form.patient &&
    form.patientMessage.trim()
  );

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      onClose={handleClose}
      open={open}>
      <DialogTitle
        sx={
          dialogHeaderStyles.dialogheader
        }>
        <Box>
          <Typography
            sx={
              dialogHeaderStyles.title
            }>
            Message
          </Typography>

          <Typography
            sx={
              dialogHeaderStyles.subtitle
            }>
            Send a secure message to the
            patient.
          </Typography>
        </Box>

        <IconButton
          aria-label="Close"
          onClick={handleClose}
          sx={
            dialogHeaderStyles
              .dialogheaderclose
          }>
          <CloseOutlined
            sx={closeIconSx}
          />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={dialogContentSx}>
        <Stack spacing={2}>
          <Typography
            sx={sectionTitleSx}>
            PATIENT
          </Typography>

          <PatientAutocomplete
            onSelectPatient={
              handleSelectPatient
            }
            patients={patients}
            value={form.patient}
            width="100%"
            placeholder={
              'Search by patient name or MR#'
            }
          />

          <TextField
            fullWidth
            label="Patient Message"
            minRows={3}
            multiline
            onChange={
              handleMessageChange
            }
            sx={multitextFieldSx}
            value={
              form.patientMessage
            }
          />

          <Button
            variant="outlined"
            startIcon={
              <AttachFileOutlined />
            }
            sx={attachButtonSx}>
            Attach Doctor&apos;s Order
          </Button>
        </Stack>
      </DialogContent>

      <CommonDialogActions
        onCancel={handleClose}
        onSubmit={
          handleSendMessage
        }
        submitText="Send Message"
        disabled={!isValid}
      />
    </Dialog>
  );
};

export const ClinicResponseModal = ({
  open,
  onClose,
  data
}) => {
  const status =
    data?.status ||
    'Not Acknowledged';

  const chipStyle =
    getStatusStyle(status);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth>
      <DialogTitle
        sx={
          dialogHeaderStyles.dialogheader
        }>
        <Box>
          <Typography
            sx={
              dialogHeaderStyles.title
            }>
            Clinic Response
          </Typography>

          <Typography
            sx={
              dialogHeaderStyles.subtitle
            }>
            Reply to a patient message.
          </Typography>
        </Box>

        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={
            dialogHeaderStyles
              .dialogheaderclose
          }>
          <CloseOutlined
            sx={closeIconSx}
          />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={dialogContentSx}>
        <Box sx={patientBoxSx}>
          <Box
            sx={
              patientHeaderRowSx
            }>
            <Box>
              <Typography
                sx={patientNameSx}>
                {data?.patient ||
                  'Philips, Cammiee John'}
              </Typography>

              <Typography
                sx={patientMrSx}>
                MR# 2318634
              </Typography>
            </Box>

            <Chip
              label={status}
              sx={
                statusChipSx(
                  chipStyle
                )
              }
            />
          </Box>
        </Box>

        <Stack spacing={1}>
          <Typography
            sx={sectionTitleSx}>
            PATIENT MESSAGE
          </Typography>

          <Typography
            sx={issueTextSx}>
            Could you also mail or fax
            me a lab order? I need to
            do my A1C and recheck my
            lipids since I have been
            on Atorvastatin for 3
            months now.
          </Typography>
        </Stack>

        <Stack
          spacing={2}
          sx={responseSectionSx}>
          <Divider />

          <TextField
            fullWidth
            multiline
            minRows={4}
            label="Clinic Response"
            defaultValue={
              'We\'ve sent the order via fax that includes your A1C test and lipid panel recheck to monitor your levels after being on Atorvastatin for the past 3 months.'
            }
            sx={multitextFieldSx}
          />

          <Button
            variant="outlined"
            startIcon={
              <AttachFileOutlined />
            }
            sx={attachButtonSx}>
            Attach Doctor&apos;s Order
          </Button>
        </Stack>
      </DialogContent>

      <CommonDialogActions
        onCancel={onClose}
        onSubmit={onClose}
        submitText="Send Response"
        disabled={false}
      />
    </Dialog>
  );
};

export const MessageTimelineModal = ({
  open,
  onClose,
  data
}) => {
  const status =
    data?.status ||
    'Not Acknowledged';

  const chipStyle =
    getStatusStyle(status);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth>
      <DialogTitle
        sx={
          dialogHeaderStyles.dialogheader
        }>
        <Box>
          <Typography
            sx={
              dialogHeaderStyles.title
            }>
            Message Timeline
          </Typography>

          <Typography
            sx={
              dialogHeaderStyles.subtitle
            }>
            Track patient and messages
            from clinic
          </Typography>
        </Box>

        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={
            dialogHeaderStyles
              .dialogheaderclose
          }>
          <CloseOutlined
            sx={closeIconSx}
          />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={dialogContentSx}>
        <Box sx={patientBoxSx}>
          <Box
            sx={
              patientHeaderRowSx
            }>
            <Box>
              <Typography
                sx={patientNameSx}>
                {data?.patient ||
                  'Willow, Gwen Marry'}
              </Typography>

              <Typography
                sx={patientMrSx}>
                MR# 2318634
              </Typography>
            </Box>

            <Chip
              label={status}
              sx={
                statusChipSx(
                  chipStyle
                )
              }
            />
          </Box>
        </Box>

        <Box sx={timelineItemSx}>
          <Box
            sx={
              timelineIconWrapperSx
            }>
            <CheckCircleOutlined
              sx={
                completedTimelineIconSx
              }
            />
          </Box>

          <Box
            sx={
              timelineContentSx
            }>
            <Typography
              sx={timelineTitleSx}>
              Patient Message
            </Typography>

            <Typography
              sx={timelineMetaSx}>
              Message to the patient
              &nbsp; • &nbsp;
              06/23/2026 at 5:30 PM
            </Typography>

            <Typography
              sx={timelineUserSx}>
              <PersonOutlined
                sx={
                  timelineUserIconSx
                }
              />

              Jennifer Lee
            </Typography>

            <Box
              sx={
                timelineMessageSx
              }>
              Dr. reviewed your
              Mammogram and stated the
              Mammogram looks good,
              nothing to be concerned,
              however, recommend repeat
              mammogram in 12 months.
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};